import * as React from 'react';

/**
 * PatientQuickViewContext – Global context for patient quick view drawer
 *
 * Global UI Design System:
 * - Enables opening patient drawer from anywhere in the app
 * - Mounted at root Layout level
 *
 * Usage:
 *   const { openPatientQuickView } = usePatientQuickView();
 *   openPatientQuickView(patientId);
 */

interface PatientQuickViewState {
  isOpen: boolean;
  patientId: string | null;
  programId: string | null;
}

interface PatientQuickViewContextValue {
  state: PatientQuickViewState;
  openPatientQuickView: (patientId: string, programId: string) => void;
  closePatientQuickView: () => void;
}

const PatientQuickViewContext = React.createContext<PatientQuickViewContextValue | null>(null);

export function PatientQuickViewProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = React.useState<PatientQuickViewState>({
    isOpen: false,
    patientId: null,
    programId: null,
  });

  const openPatientQuickView = React.useCallback((patientId: string, programId: string) => {
    setState({
      isOpen: true,
      patientId,
      programId,
    });
  }, []);

  const closePatientQuickView = React.useCallback(() => {
    setState((prev) => ({
      ...prev,
      isOpen: false,
    }));
  }, []);

  const value = React.useMemo(
    () => ({
      state,
      openPatientQuickView,
      closePatientQuickView,
    }),
    [state, openPatientQuickView, closePatientQuickView]
  );

  return (
    <PatientQuickViewContext.Provider value={value}>
      {children}
    </PatientQuickViewContext.Provider>
  );
}

export function usePatientQuickView() {
  const context = React.useContext(PatientQuickViewContext);
  if (!context) {
    throw new Error('usePatientQuickView must be used within PatientQuickViewProvider');
  }
  return context;
}

/**
 * PatientNameLink – Clickable patient name that opens quick view
 *
 * Usage:
 *   <PatientNameLink patientId={id} programId={pid}>
 *     John Doe
 *   </PatientNameLink>
 */
interface PatientNameLinkProps {
  patientId: string;
  programId: string;
  children: React.ReactNode;
  className?: string;
}

export function PatientNameLink({
  patientId,
  programId,
  children,
  className,
}: PatientNameLinkProps) {
  const { openPatientQuickView } = usePatientQuickView();

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    openPatientQuickView(patientId, programId);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={`text-primary hover:text-primary-hover hover:underline cursor-pointer text-left font-medium transition-colors ${className || ''}`}
    >
      {children}
    </button>
  );
}

export { PatientQuickViewContext };
export default PatientQuickViewProvider;
